import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Sparkles, Send, Trash2, Pencil, Check, X, RefreshCw, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import type { NegotiationMessage } from "@/types/negotiations";

interface DraftCardProps {
  message: NegotiationMessage;
  threadId: string;
  onApproveSend: (messageId: string) => Promise<void>;
  onDiscard: (messageId: string) => Promise<void>;
  onRegenerate: (threadId: string) => Promise<void>;
  onEdit: (messageId: string, updates: { subject?: string; body_text?: string }) => Promise<void>;
  isSending: boolean;
  isDiscarding: boolean;
  isRegenerating: boolean;
}

export function DraftCard({
  message,
  threadId,
  onApproveSend,
  onDiscard,
  onRegenerate,
  onEdit,
  isSending,
  isDiscarding,
  isRegenerating,
}: DraftCardProps) {
  const [editing, setEditing] = useState(false);
  const [editSubject, setEditSubject] = useState(message.subject ?? "");
  const [editBody, setEditBody] = useState(message.body_text ?? "");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const [rationaleOpen, setRationaleOpen] = useState(false);

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      await onEdit(message.id, { subject: editSubject, body_text: editBody });
      setEditing(false);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleCancelEdit = () => {
    setEditSubject(message.subject ?? "");
    setEditBody(message.body_text ?? "");
    setEditing(false);
  };

  const busy = isSending || isDiscarding || isRegenerating || isSavingEdit;

  // Clamp preview: show ~1 line (80 chars) when not expanded — keeps the draft
  // card very short so the conversation history above gets most of the pane.
  const bodyText = message.body_text ?? "(no body)";
  const CLAMP_LEN = 80;
  const isClamped = !bodyExpanded && bodyText.length > CLAMP_LEN;
  const previewText = isClamped ? bodyText.slice(0, CLAMP_LEN).trimEnd() + "…" : bodyText;

  return (
    <Card className="border border-warning/30 bg-warning/5 p-3 space-y-2">
      {/* Title row — label + secondary actions on right */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 text-xs font-medium text-warning">
          <Sparkles className="w-3.5 h-3.5" />
          AI Draft — Pending Approval
        </div>
        <div className="flex items-center gap-0.5">
          {!editing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
              onClick={() => setEditing(true)}
              disabled={busy}
            >
              <Pencil className="w-3 h-3 mr-1" />
              Edit
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => onRegenerate(threadId)}
            disabled={busy}
          >
            <RefreshCw className={cn("w-3 h-3 mr-1", isRegenerating && "animate-spin")} />
            Regenerate
          </Button>
        </div>
      </div>

      {/* Content */}
      {editing ? (
        <div className="space-y-2">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Subject</Label>
            <Input
              value={editSubject}
              onChange={(e) => setEditSubject(e.target.value)}
              className="h-7 text-xs bg-background"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Body</Label>
            <Textarea
              value={editBody}
              onChange={(e) => setEditBody(e.target.value)}
              rows={5}
              className="text-xs bg-background resize-none"
            />
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-6 text-xs"
              onClick={handleSaveEdit}
              disabled={isSavingEdit}
            >
              <Check className="w-3 h-3 mr-1" />
              Save
            </Button>
            <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={handleCancelEdit}>
              <X className="w-3 h-3 mr-1" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        /* Clamped body preview — ~1-line default; expanded body is capped at
           max-h-[15vh] with its own scroll so a long draft can't push other
           UI out of the bottom cluster. */
        <div
          className={cn(
            "text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed bg-muted/20 rounded-md p-2",
            bodyExpanded && "max-h-[15vh] overflow-y-auto"
          )}
        >
          {previewText}
          {bodyText.length > CLAMP_LEN && (
            <button
              className="ml-1 text-primary text-[11px] hover:underline focus:outline-none"
              onClick={() => setBodyExpanded((v) => !v)}
            >
              {bodyExpanded ? "Show less" : "Show full draft"}
            </button>
          )}
        </div>
      )}

      {/* AI rationale — collapsed by default */}
      {message.ai_rationale && !editing && (
        <Collapsible open={rationaleOpen} onOpenChange={setRationaleOpen}>
          <CollapsibleTrigger asChild>
            <button
              data-testid="rationale-toggle"
              className="flex items-center gap-1 text-[11px] text-muted-foreground/70 hover:text-muted-foreground transition-colors"
            >
              <ChevronDown
                className={cn("w-3 h-3 transition-transform", rationaleOpen && "rotate-180")}
              />
              Why this draft?
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="text-[11px] text-muted-foreground/70 italic pt-1 pl-4">
              {message.ai_rationale}
            </p>
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Action buttons — compact single row */}
      {!editing && (
        <div className="flex items-center gap-2 pt-1 border-t border-border">
          <Button
            size="sm"
            className="h-7 text-xs bg-primary hover:bg-primary/90 flex-1"
            onClick={() => onApproveSend(message.id)}
            disabled={busy}
          >
            <Send className="w-3 h-3 mr-1" />
            {isSending ? "Sending…" : "Approve & Send"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs text-destructive hover:text-destructive"
            onClick={() => onDiscard(message.id)}
            disabled={busy}
          >
            <Trash2 className="w-3 h-3 mr-1" />
            {isDiscarding ? "Discarding…" : "Discard"}
          </Button>
        </div>
      )}
    </Card>
  );
}
